import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2, AlertTriangle, ArrowLeft, ShieldCheck } from "lucide-react";
import ParticleBackground from "@/components/ParticleBackground";
const logo = "/assets/Touchworks-b.svg";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // States
  const [step, setStep] = useState("form"); // form | success | error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");

  // Password fields
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation (same rules as VerifyEmail.jsx)
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    match: password === confirmPassword && password.length > 0,
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  // Validate token exists on mount
  useEffect(() => {
    if (!token) {
      setStep("error");
      setError("Link de recuperação inválido. Verifique o link no seu email.");
      setErrorCode("INVALID_TOKEN");
    }
  }, [token]);

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!isPasswordValid || !token) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === "TOKEN_EXPIRED") {
          setStep("error");
          setError("Este link expirou. Solicite um novo link de recuperação.");
          setErrorCode("TOKEN_EXPIRED");
        } else if (data.code === "INVALID_TOKEN") {
          setStep("error");
          setError("Link inválido ou já utilizado.");
          setErrorCode("INVALID_TOKEN");
        } else {
          setError(data.error || "Erro ao redefinir senha.");
        }
        return;
      }

      setStep("success");
    } catch {
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }

  const CheckItem = ({ checked, label }) => (
    <div className="flex items-center gap-2">
      {checked ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground/50" />
      )}
      <span className={`text-sm ${checked ? "text-emerald-400" : "text-muted-foreground/50"}`}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6 md:p-10 bg-background text-foreground font-['Space_Grotesk'] relative overflow-hidden">
      <ParticleBackground />

      <div className="relative z-20 w-full max-w-sm md:max-w-lg">
        <Card className="overflow-hidden border border-white/5 bg-surface/95 backdrop-blur-sm shadow-2xl">
          <CardContent className="p-6 md:p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img src={logo} alt="Touchworks Logo" className="h-7 w-auto" />
            </div>

            {/* ── Step: Form ── */}
            {step === "form" && (
              <div className="flex flex-col gap-6">
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500/10 border border-cyan-500/20">
                    <ShieldCheck className="h-8 w-8 text-cyan-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground-strong">Redefinir senha</h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    Crie uma nova senha segura para sua conta.
                  </p>
                </div>

                <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
                  {/* New Password */}
                  <div className="grid gap-2">
                    <Label htmlFor="password" className="text-foreground-strong">Nova senha</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Digite sua nova senha"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="bg-background border-border text-foreground-strong placeholder:text-placeholder pr-10 focus-visible:ring-cyan-500 h-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground-strong"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="grid gap-2">
                    <Label htmlFor="confirmPassword" className="text-foreground-strong">Confirmar senha</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirme sua nova senha"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-background border-border text-foreground-strong placeholder:text-placeholder pr-10 focus-visible:ring-cyan-500 h-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground-strong"
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Password Validation Checks */}
                  {password.length > 0 && (
                    <div className="bg-background border border-border rounded-lg p-4 space-y-2">
                      <CheckItem checked={passwordChecks.length} label="Mínimo 8 caracteres" />
                      <CheckItem checked={passwordChecks.uppercase} label="Uma letra maiúscula" />
                      <CheckItem checked={passwordChecks.number} label="Um número" />
                      <CheckItem checked={passwordChecks.match} label="Senhas conferem" />
                    </div>
                  )}

                  {error && step === "form" && (
                    <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border border-red-500/30 dark:border-red-500/20 rounded-lg p-3">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={!isPasswordValid || isLoading}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold h-10 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redefinindo...
                      </>
                    ) : (
                      "Redefinir senha"
                    )}
                  </Button>
                </form>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-sm text-cyan-500 hover:text-cyan-400 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar ao login
                  </Link>
                </div>
              </div>
            )}

            {/* ── Step: Success ── */}
            {step === "success" && (
              <div className="flex flex-col items-center gap-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground-strong mb-2">
                    Senha redefinida!
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Sua senha foi alterada com sucesso. Agora você pode fazer login
                    com sua nova senha.
                  </p>
                </div>

                <Button
                  onClick={() => navigate("/login")}
                  className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold h-10"
                >
                  Ir para o login
                </Button>
              </div>
            )}

            {/* ── Step: Error ── */}
            {step === "error" && (
              <div className="flex flex-col items-center gap-6 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 dark:bg-red-500/10 border border-red-500/30 dark:border-red-500/20">
                  <AlertTriangle className="h-8 w-8 text-red-700 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground-strong mb-2">
                    {errorCode === "TOKEN_EXPIRED" ? "Link expirado" : "Link inválido"}
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    {error}
                  </p>
                </div>

                <div className="flex flex-col gap-2 w-full">
                  <Button
                    onClick={() => navigate("/forgot-password")}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold h-10"
                  >
                    Solicitar novo link
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/login")}
                    className="w-full text-muted-foreground hover:text-foreground-strong"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Voltar ao login
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground mt-6">
          Precisa de ajuda?{" "}
          <a
            href="https://wa.me/551152867786"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground-strong"
          >
            Fale conosco
          </a>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 z-50"></div>
    </div>
  );
}
